export const javaDefaultSource = `import java.util.ArrayList;
import java.util.List;
import java.util.HashMap;
import java.util.Map;

public class LegacyCollectionExample {
    
    public void processItems() {
        List<String> items = new ArrayList<>();
        items.add("apple");
        items.add("banana");
        items.add("cherry");
        
        // Old style iteration and filtering
        List<String> filteredItems = new ArrayList<>();
        for (String item : items) {
            if (item.startsWith("a")) {
                filteredItems.add(item.toUpperCase());
            }
        }
        
        // Old style mapping
        List<Integer> lengths = new ArrayList<>();
        for (String item : items) {
            lengths.add(item.length());
        }
        
        // Old style aggregation
        int total = 0;
        for (Integer length : lengths) {
            total += length;
        }
        
        // Already using modern style
        items.stream()
            .filter(s -> s.contains("a"))
            .forEach(System.out::println);
    }
    
    public void handleMap() {
        Map<String, Integer> scores = new HashMap<>();
        scores.put("Alice", 95);
        scores.put("Bob", 80);
        scores.put("Charlie", 90);
        
        // Old style map iteration
        for (Map.Entry<String, Integer> entry : scores.entrySet()) {
            if (entry.getValue() > 85) {
                System.out.println(entry.getKey() + " is a top student");
            }
        }
    }
}`;

export const javaDefaultCodemod = `import { type SgRoot } from "@ast-grep/napi";

async function transform(root: SgRoot): Promise<string | null> {
  const rootNode = root.root();

  // Find for-each loops that iterate over a collection and add items to another collection
  const filterMapLoops = rootNode.findAll({
    rule: {
      pattern: \`
        for ($TYPE $VAR : $COLLECTION) {
          if ($CONDITION) {
            $TARGET.add($EXPRESSION);
          }
        }
      \`
    }
  });

  const edits = filterMapLoops.map(node => {
    const type = node.getMatch("TYPE").text();
    const variable = node.getMatch("VAR").text();
    const collection = node.getMatch("COLLECTION").text();
    const condition = node.getMatch("CONDITION").text();
    const target = node.getMatch("TARGET").text();
    const expression = node.getMatch("EXPRESSION").text();
    
    // Transform to streams API
    return node.replace(\`
      \${collection}.stream()
          .filter(\${variable} -> \${condition})
          .map(\${variable} -> \${expression})
          .forEach(item -> \${target}.add(item));
    \`);
  });

  // Find simple for-each loops that just map one collection to another
  const simpleMappingLoops = rootNode.findAll({
    rule: {
      pattern: \`
        for ($TYPE $VAR : $COLLECTION) {
          $TARGET.add($EXPRESSION);
        }
      \`
    }
  });

  const moreEdits = simpleMappingLoops.map(node => {
    const variable = node.getMatch("VAR").text();
    const collection = node.getMatch("COLLECTION").text();
    const target = node.getMatch("TARGET").text();
    const expression = node.getMatch("EXPRESSION").text();
    
    // Transform to streams API
    return node.replace(\`
      \${collection}.stream()
          .map(\${variable} -> \${expression})
          .forEach(item -> \${target}.add(item));
    \`);
  });

  // Apply all changes
  const newSource = rootNode.commitEdits([...edits, ...moreEdits]);
  return newSource;
}

export default transform;
`;

export const javaDefaultConfig = `
id: java-for-loop-to-streams
language: java
rule:
  pattern: |
    for ($TYPE $VAR : $COLLECTION) {
      if ($CONDITION) {
        $TARGET.add($EXPRESSION);
      }
    }
fix: |
  $COLLECTION.stream()
      .filter($VAR -> $CONDITION)
      .map($VAR -> $EXPRESSION)
      .forEach(item -> $TARGET.add(item));
`.trim();
